/**
 * Intake-rule application alert codes and their user-facing text.
 *
 * Lives in `@alinea/types`, not `@erp/services`, deliberately: the web bill-detail alert list
 * renders these strings, and `INTAKE_RULE_APPLICATION_ALERT_MESSAGES` is a value, so importing it
 * from the `@erp/services/ap` barrel pulled that barrel's server dependencies into the Vite
 * bundle — `@alinea/db` re-exports `advisory-lock.ts`, whose top-level `createRequire` from
 * `node:module` cannot exist in a browser. It crashed every bill detail page, and no test caught it
 * because vitest runs in Node where `node:module` resolves fine. Same reasoning as
 * `contract-review-flag-messages.ts` and the note in
 * `apps/web/src/lib/reimbursement-intake-receipt-alerts.ts`.
 *
 * Every code must carry pt-BR text as well as English: the exhaustive `Record` below makes a
 * missing translation a compile error.
 */
export const IntakeRuleApplicationAlertCode = {
  INTAKE_RULE_TARGET_UNAVAILABLE: 'INTAKE_RULE_TARGET_UNAVAILABLE',
  INTAKE_RULE_TARGET_ENTITY_MISMATCH: 'INTAKE_RULE_TARGET_ENTITY_MISMATCH',
  INTAKE_RULE_BANK_ACCOUNT_UNAVAILABLE: 'INTAKE_RULE_BANK_ACCOUNT_UNAVAILABLE',
  INTAKE_RULE_TARGET_INVALID_VALUE: 'INTAKE_RULE_TARGET_INVALID_VALUE',
  INTAKE_RULE_PAYMENT_APPLICATION_FAILED: 'INTAKE_RULE_PAYMENT_APPLICATION_FAILED',
  INTAKE_RULE_CLASSIFICATION_BYPASSED: 'INTAKE_RULE_CLASSIFICATION_BYPASSED',
  INTAKE_RULE_CLASSIFICATION_DIMENSION_DROPPED: 'INTAKE_RULE_CLASSIFICATION_DIMENSION_DROPPED',
  INTAKE_RULE_CLASSIFICATION_APPLICATION_FAILED: 'INTAKE_RULE_CLASSIFICATION_APPLICATION_FAILED',
} as const;
export type IntakeRuleApplicationAlertCode =
  (typeof IntakeRuleApplicationAlertCode)[keyof typeof IntakeRuleApplicationAlertCode];

export const INTAKE_RULE_APPLICATION_ALERT_MESSAGES: Record<
  IntakeRuleApplicationAlertCode,
  { ptBr: string; en: string }
> = {
  INTAKE_RULE_TARGET_UNAVAILABLE: {
    ptBr: 'O destino configurado pela regra de entrada não está mais disponível.',
    en: 'The target configured by the intake rule is no longer available.',
  },
  INTAKE_RULE_TARGET_ENTITY_MISMATCH: {
    ptBr: 'O destino configurado pela regra de entrada não pertence à entidade da conta.',
    en: 'The target configured by the intake rule does not belong to the bill entity.',
  },
  INTAKE_RULE_BANK_ACCOUNT_UNAVAILABLE: {
    ptBr: 'A conta bancária de origem configurada pela regra de entrada não está disponível.',
    en: 'The origin bank account configured by the intake rule is unavailable.',
  },
  INTAKE_RULE_TARGET_INVALID_VALUE: {
    ptBr: 'O valor configurado pela regra de entrada não é válido para este campo.',
    en: 'The value configured by the intake rule is invalid for this field.',
  },
  INTAKE_RULE_PAYMENT_APPLICATION_FAILED: {
    ptBr: 'O pagamento da regra de entrada não pôde ser aplicado à conta.',
    en: 'The intake-rule payment could not be applied to the bill.',
  },
  INTAKE_RULE_CLASSIFICATION_BYPASSED: {
    ptBr: 'A classificação da regra de entrada não foi aplicada porque esta conta ignora a classificação automática.',
    en: 'The intake-rule classification was not applied because this bill bypasses automatic classification.',
  },
  INTAKE_RULE_CLASSIFICATION_DIMENSION_DROPPED: {
    ptBr: 'A dimensão da regra de entrada foi descartada durante a nova tentativa de salvar a classificação.',
    en: 'The intake-rule dimension was dropped while retrying the classification save.',
  },
  INTAKE_RULE_CLASSIFICATION_APPLICATION_FAILED: {
    ptBr: 'A classificação da regra de entrada não pôde ser salva na conta.',
    en: 'The intake-rule classification could not be saved to the bill.',
  },
};
